/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mergeMap, takeUntil, tap } from 'rxjs/operators';

import { PatientService } from '@sondermind/data-access/patient';
import { SondermindApplicationService } from '@sondermind/data-access/sondermind-app-service';
import { BreakpointsService } from '@sondermind/formlib';
import { IrisButtonContainerAlignments, IrisButtonTypes } from '@sondermind/iris/button';
import { ServerResponse } from '@sondermind/utilities/models-http';
import { Patient } from '@sondermind/utilities/models-patient';
import { ProviderUser } from '@sondermind/utilities/models-user-provider';
import { LUXON_DATE_FORMAT, toTimeZoneDateTime } from '@sondermind/utilities/time-and-date';
import { AtLeastOneValidator } from '@sondermind/utilities/tools/validators/at-least-one.validator';
import { ConditionalValidator } from '@sondermind/utilities/tools/validators/conditional.validator';
import { EmailValidator } from '@sondermind/utilities/tools/validators/email.validator';

@Component({
  selector: 'calendar-embedded-client-creator',
  templateUrl: './embedded-client-creator.component.html',
  styleUrls: ['./embedded-client-creator.component.scss'],
  standalone: false
})
export class EmbeddedClientCreatorComponent implements OnInit, OnDestroy {
  @Input() user: ProviderUser = new ProviderUser();
  @Input() cancelable: boolean = true;

  saving = false;
  private defaultFormValue: any = null;

  form: UntypedFormGroup = null;

  @Output() patientCreated = new EventEmitter<Patient>();
  @Output() canceled = new EventEmitter<boolean>();

  irisButtonTypes = IrisButtonTypes;
  irisButtonContainerAlignments = IrisButtonContainerAlignments;

  isMobile$ = this._breakpoint.isMobile$;

  private destroyed$ = new EventEmitter<void>();

  constructor(
    private _appService: SondermindApplicationService,
    private _patientService: PatientService,
    private formBuilder: UntypedFormBuilder,
    private snackBar: MatSnackBar,
    private _breakpoint: BreakpointsService
  ) { }

  handleSave(event: any): void {
    if (this.form.invalid) { return }

    this.saving = true;
    const p = new Patient();
    const data = this.form.value;
    p.firstName = data.firstName;
    p.lastName = data.lastName;
    p.email = data.email;
    p.nonSpnPatient = data.nonSpnPatient;
    // Pass the date of birth to the backend in 'MM/DD/YYYY' format.
    p.dateOfBirth = null;
    if (data.dateOfBirth instanceof Date) {
      p.dateOfBirth = toTimeZoneDateTime(
        data.dateOfBirth,
        this._appService.currentUser.timeZone
      ).toFormat(LUXON_DATE_FORMAT);
    }

    if (!data.nonSpnPatient) {
      this._patientService.create(this.user.id, p)
        .pipe(
          tap((x) => {
            p.id = x.value.id;
          }),
          mergeMap(() => this._patientService.sendInvitation(this._appService.currentUser.id, p, data))
        ).subscribe({
          next: (resp) => {
            this.success(resp);
          },
          error: (err) => {
            this.error(err);
          }
        });
    } else {
      this._patientService.create(this.user.id, p)
        .subscribe({
          next: (resp) => {
            this.success(resp);
          },
          error: (err) => {
            this.error(err);
          }
        });
    }
  }

  success(response: ServerResponse<Patient, any>): void {
    this.patientCreated.emit(response.value);
    this.reset();
    this.saving = false;
  }

  error(error: any): void {
    this.snackBar.open('Sorry something went wrong', 'Dismiss');
    // eslint-disable-next-line no-console
    console.error(error);
    this.saving = false;
  }

  handleCancel(event: any): void {
    this.reset();
    this.canceled.emit(true);
  }

  ngOnInit(): void {
    this.form = this.buildForm();

    // save off default for when we reset
    this.defaultFormValue = this.form.value;

    this.form.controls['nonSpnPatient'].valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((v) => {
        Object.keys(this.form.controls).forEach((k) => {
          this.form.controls[k].updateValueAndValidity({ onlySelf: true, emitEvent: k !== 'nonSpnPatient' });
        });
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
  }

  get valid(): boolean {
    return this.form.valid;
  }

  reset(): void {
    this.form.reset(this.defaultFormValue);
  }

  /**
   * Build form according to the invite client variant.
   */
  private buildForm(): UntypedFormGroup {
    const compose = Validators.compose;
    const validateWhenNot = ConditionalValidator.validateWhenNot;
    const { required } = Validators;
    const isNonSpnPatient = !this._appService.currentUser.canSubmitSuperbill;

    const formControls = {
      firstName: [null, validateWhenNot('nonSpnPatient', required)],
      lastName: [null, validateWhenNot('nonSpnPatient', required)],
      email: [null, compose([EmailValidator.format, validateWhenNot('nonSpnPatient', required)])],
      nonSpnPatient: [isNonSpnPatient],
    };

    const validators = [
      AtLeastOneValidator.of(['firstName', 'lastName'], { base: 'A first or last name is required!' })
    ];

    const formGroup: UntypedFormGroup = this.formBuilder.group(
      formControls,
      {
        validator: compose(
          validators
        )
      }
    );

    return formGroup;
  }
}
